import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Archive,
  Calendar,
  PhilippinePeso,
  Download,
  Eye,
  FileText,
  GraduationCap,
  User,
  Building,
  Trash2,
  Edit,
  RotateCcw,
} from "lucide-react";
import {
  Project,
  User as UserType,
  Material,
  WorkLogEntry,
  ProjectAttachment,
} from "../../types";
import { ProjectDetails } from "../projects/ProjectDetails";
import Swal from "sweetalert2";
import { archivedProjects, canArchiveProject } from "../../utils/projectArchive";

interface ProjectArchivesProps {
  projects: Project[];
  users: UserType[];
  materials: Material[];
  workLogs: WorkLogEntry[];
  currentUser: UserType;
  onUpdateProject?: (project: Project) => void;
  onDeleteProject?: (projectId: string) => void;
  onRestoreProject?: (projectId: string) => Promise<Project>;
  onCreateProject?: (projectData: any) => Promise<any>;
}

export function ProjectArchives({
  projects,
  users,
  materials,
  workLogs,
  currentUser,
  onUpdateProject,
  onDeleteProject,
  onRestoreProject,
  onCreateProject: _onCreateProject,
}: ProjectArchivesProps) {
  const safeText = (value?: string) => value || "";
  const safeNumber = (value: unknown) => {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string") {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
  };
  const getProjectSchool = (project: Project) => {
    const client = users.find(
      (u) => u.role === "client" && u.clientProjectId === project.id,
    );
    const name = safeText(client?.school || project.clientName).trim();
    return name || "Unknown";
  };
  const formatDate = (value?: string) => {
    if (!value) return "N/A";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "N/A";
    return date.toLocaleDateString();
  };
  const downloadAttachment = (attachment: ProjectAttachment) => {
    if (!attachment.url) return;
    const link = document.createElement("a");
    link.href = attachment.url;
    link.download = attachment.name || "attachment";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };
  const getAttachments = (project?: Project) =>
    Array.isArray(project?.attachments) ? project!.attachments! : [];
  const getFabricatorIds = (project?: Project) =>
    Array.isArray(project?.fabricatorIds) ? project!.fabricatorIds : [];

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSchool, setSelectedSchool] = useState("all");
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);

  const completedProjects = archivedProjects(projects);

  // Get unique schools from completed projects
  const schools = Array.from(
    new Set(completedProjects.map(getProjectSchool)),
  ).sort();

  // Filter projects based on search and school
  const filteredProjects = completedProjects.filter((project) => {
    const matchesSearch =
      safeText(project.name).toLowerCase().includes(searchTerm.toLowerCase()) ||
      safeText(project.description)
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      safeText(project.clientName)
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

    const projectSchool = getProjectSchool(project);
    const matchesSchool =
      selectedSchool === "all" || projectSchool === selectedSchool;

    return matchesSearch && matchesSchool;
  });

  const handleViewDetails = (project: Project) => {
    setSelectedProject(project);
    setShowDetailsDialog(true);
  };

  const getProjectMaterials = (projectId: string) => {
    return materials.filter((material) => material.projectId === projectId);
  };

  const getProjectWorkLogs = (projectId: string) => {
    return workLogs.filter((log) => log.projectId === projectId);
  };

  const calculateTotalMaterialCost = (projectId: string) => {
    return getProjectMaterials(projectId).reduce(
      (total, material) => total + safeNumber(material.cost),
      0,
    );
  };

  const getFabricatorDocumentation = (project: Project) => {
    const attachments = getAttachments(project);
    const fabricatorIds = getFabricatorIds(project);
    return attachments.filter((att) =>
      fabricatorIds.some((fabId) => att.uploadedBy === fabId),
    );
  };

  const getCostAnalysisDocuments = (project: Project) => {
    const attachments = getAttachments(project);
    return attachments.filter(
      (att) =>
        safeText(att.name).toLowerCase().includes("cost") ||
        safeText(att.name).toLowerCase().includes("analysis") ||
        safeText(att.name).toLowerCase().includes("budget") ||
        att.type?.includes("spreadsheet"),
    );
  };

  const canViewFinancials = currentUser.role === "admin";

  const handleDeleteClick = (project: Project) => {
    Swal.fire({
      title: "Are you sure?",
      text: `Are you sure you want to delete "${project.name}"? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Confirm",
      customClass: {
        container: "swal-container !z-[10000]",
        popup: "swal-popup swal-actions-right",
        title: "swal-title",
        htmlContainer: "swal-content",
        confirmButton: "swal-confirm-button",
        cancelButton: "swal-cancel-button",
        icon: "swal-icon",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: "Deleting...",
          text: "Please wait...",
          allowOutsideClick: false,
          showConfirmButton: false,
          customClass: {
            container: "swal-container !z-[10000]",
            popup: "swal-popup swal-actions-right",
            title: "swal-title",
            htmlContainer: "swal-content",
            confirmButton: "swal-confirm-button",
            cancelButton: "swal-cancel-button",
            icon: "swal-icon",
          },
          didOpen: () => {
            Swal.showLoading();
          },
        });

        setTimeout(() => {
          if (onDeleteProject) {
            onDeleteProject(project.id);
          }
          Swal.fire({
            title: "Deleted!",
            text: "The project has been deleted.",
            icon: "success",
            customClass: {
              container: "swal-container !z-[10000]",
              popup: "swal-popup swal-actions-right",
              title: "swal-title",
              htmlContainer: "swal-content",
              confirmButton: "swal-confirm-button",
              cancelButton: "swal-cancel-button",
              icon: "swal-icon",
            },
          });
        }, 1500);
      }
    });
  };

  const handleRestoreClick = async (project: Project) => {
    if (!onRestoreProject) return;
    const result = await Swal.fire({
      title: "Restore project?",
      text: `“${project.name}” will return to the active Projects page with its previous workflow status.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Restore project",
      cancelButtonText: "Keep archived",
    });
    if (!result.isConfirmed) return;

    try {
      await onRestoreProject(project.id);
      await Swal.fire({
        title: "Project restored",
        text: "The project is active again.",
        icon: "success",
      });
    } catch (error) {
      await Swal.fire({
        title: "Restore failed",
        text: error instanceof Error ? error.message : "Unable to restore project.",
        icon: "error",
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <Archive className="h-6 w-6 text-gray-600" />
            <h1 className="text-3xl font-bold tracking-tight">
              Project Archives
            </h1>
          </div>
          <p className="text-sm text-muted-foreground">
            Completed and cancelled projects retained for reference
          </p>
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
          <Badge
            variant="secondary"
            className="h-9 px-3 text-sm w-full sm:w-auto text-center"
          >
            {filteredProjects.length} Archived Projects
          </Badge>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4 sm:p-6 border border-border/60  rounded-[24px]">
        <CardHeader className="p-0">
          <CardTitle className="text-lg font-semibold">Filter Archives</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="search">Search Projects</Label>
              <Input
                id="search"
                type="search"
                placeholder="Search by name, description, or client..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="school">Filter by School/Client</Label>
              <Select value={selectedSchool} onValueChange={setSelectedSchool}>
                <SelectTrigger>
                  <SelectValue placeholder="Select school/client" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Schools/Clients</SelectItem>
                  {schools.map((school) => (
                    <SelectItem key={school} value={school}>
                      {school}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Project Grid */}
      <div className="grid gap-6">
        {filteredProjects.map((project) => {
          const supervisor = users.find((u) => u.id === project.supervisorId);
          const fabricators = users.filter((u) =>
            getFabricatorIds(project).includes(u.id),
          );
          const projectSchool = getProjectSchool(project);
          const fabricatorDocs = getFabricatorDocumentation(project);
          const costDocs = getCostAnalysisDocuments(project);
          const materialCost = calculateTotalMaterialCost(project.id);
          const projectBudget = safeNumber(project.budget);
          const projectSpent = safeNumber(project.spent);
          const projectRevenue = safeNumber(project.revenue);

          return (
            <Card
              key={project.id}
              className="hover: - p-4 sm:p-6 border border-border/60 bg-card rounded-[24px]"
            >
              <CardHeader className="p-0">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-xl font-semibold">
                      {safeText(project.name)}
                    </CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      {safeText(project.description)}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2 sm:ml-4">
                    <Badge
                      variant={project.archiveOutcome === "cancelled" ? "destructive" : "default"}
                      className={project.archiveOutcome === "completed" ? "bg-green-600" : ""}
                    >
                      {project.archiveOutcome === "cancelled" ? "Cancelled" : "Completed"}
                    </Badge>
                    <Badge variant="outline">{project.priority}</Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4">
                  {(project.archiveReason || project.archiveNotes) && (
                    <div className="rounded-lg border border-border/60 bg-muted/40 p-3 text-sm">
                      {project.archiveReason && (
                        <p>
                          <span className="font-semibold">Closure reason:</span>{" "}
                          {project.archiveReason}
                        </p>
                      )}
                      {project.archiveNotes && (
                        <p className="mt-1 text-muted-foreground">
                          {project.archiveNotes}
                        </p>
                      )}
                    </div>
                  )}
                  {/* Project Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <GraduationCap className="h-4 w-4 text-muted-foreground" />
                      <span>{projectSchool}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span>Supervisor: {supervisor?.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span>
                        {formatDate(project.startDate)} -{" "}
                        {formatDate(project.endDate)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Building className="h-4 w-4 text-muted-foreground" />
                      <span>{fabricators.length} Fabricators</span>
                    </div>
                  </div>

                  {/* Financial Summary */}
                  {canViewFinancials && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-muted rounded-sm">
                      <div className="text-center">
                        <p className="text-sm text-muted-foreground">Budget</p>
                        <p className="font-medium">
                          ₱{projectBudget.toLocaleString()}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-muted-foreground">Spent</p>
                        <p className="font-medium">
                          ₱{projectSpent.toLocaleString()}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-muted-foreground">
                          Materials
                        </p>
                        <p className="font-medium">
                          ₱{materialCost.toLocaleString()}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-muted-foreground">Revenue</p>
                        <p className="font-medium text-green-600">
                          ₱{projectRevenue.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Documentation Summary */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-primary" />
                      <span className="text-sm">
                        {fabricatorDocs.length} Fabricator Documents
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <PhilippinePeso className="h-4 w-4 text-green-600" />
                      <span className="text-sm">
                        {costDocs.length} Cost Analysis Files
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Archive className="h-4 w-4 text-primary" />
                      <span className="text-sm">
                        {getAttachments(project).length} Total Documents
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="grid grid-cols-2 lg:flex gap-2 pt-2 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full lg:flex-1 bg-green-200 hover:bg-green-400"
                      onClick={() => handleViewDetails(project)}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      <span className="truncate">Details</span>
                    </Button>
                    {project.documentationUrl && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full lg:flex-1"
                        onClick={() =>
                          window.open(project.documentationUrl, "_blank")
                        }
                      >
                        <Download className="h-4 w-4 mr-2" />
                        <span className="truncate">Docs</span>
                      </Button>
                    )}
                    {canArchiveProject(currentUser) && onRestoreProject && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full lg:flex-1"
                        onClick={() => handleRestoreClick(project)}
                      >
                        <RotateCcw className="mr-2 size-4" aria-hidden="true" />
                        <span className="truncate">Restore</span>
                      </Button>
                    )}
                    {(currentUser.role === "admin" ||
                      currentUser.role === "supervisor") && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full lg:flex-1 text-primary bg-blue-200 hover:bg-blue-400"
                          onClick={() => {
                            setSelectedProject(project);
                            setShowEditDialog(true);
                          }}
                        >
                          <Edit className="h-4 w-4 mr-2 dark:text-white" />
                          <span className="truncate dark:text-white">Edit</span>
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full lg:flex-1 bg-red-200 text-destructive hover:text-white hover:bg-red-400"
                          onClick={() => handleDeleteClick(project)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          <span className="truncate">Delete</span>
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredProjects.length === 0 && (
        <Card className="p-4 sm:p-6 rounded-[24px]">
          <CardContent>
            <div className="text-center py-12">
              <Archive className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg mb-2">No Archived Projects Found</h3>
              <p className="text-muted-foreground">
                {searchTerm || selectedSchool !== "all"
                  ? "Try adjusting your search filters."
                  : "No projects have been archived yet."}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Project Details Dialog */}
      {/* Project Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
      <DialogContent className="max-w-[calc(100%-2rem)] sm:max-w-2xl max-h-[80vh] p-6 sm:p-8">
          <DialogHeader>
            <DialogTitle>{selectedProject?.name}</DialogTitle>
            <DialogDescription>
              Completed project details and documentation
            </DialogDescription>
          </DialogHeader>

          {selectedProject && (
            <div className="space-y-6">
              <Tabs defaultValue="overview" className="w-full">
                <TabsList className="grid w-full grid-cols-4 gap-2 sm:gap-6">
                  <TabsTrigger value="overview">
                    <Eye className="h-4 w-4 sm:hidden" />
                    <span className="hidden sm:inline">Overview</span>
                  </TabsTrigger>
                  <TabsTrigger value="documentation">
                    <FileText className="h-4 w-4 sm:hidden" />
                    <span className="hidden sm:inline">Documentation</span>
                  </TabsTrigger>
                  <TabsTrigger value="costs">
                    <PhilippinePeso className="h-4 w-4 sm:hidden" />
                    <span className="hidden sm:inline">Cost Analysis</span>
                  </TabsTrigger>
                  <TabsTrigger value="team">
                    <User className="h-4 w-4 sm:hidden" />
                    <span className="hidden sm:inline">Team</span>
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Project Description</Label>
                      <p className="text-sm mt-1 overflow-hidden text-ellipsis">
                        {safeText(selectedProject.description)}
                      </p>
                    </div>
                    <div>
                      <Label>Client/School</Label>
                      <p className="text-sm mt-1">
                        {users.find(
                          (u) =>
                            u.role === "client" &&
                            u.clientProjectId === selectedProject.id,
                        )?.school || safeText(selectedProject.clientName)}
                      </p>
                    </div>
                    <div>
                      <Label>Project Duration</Label>
                      <p className="text-sm mt-1">
                        {formatDate(selectedProject.startDate)} -{" "}
                        {formatDate(selectedProject.endDate)}
                      </p>
                    </div>
                    <div>
                      <Label>Completion Date</Label>
                      <p className="text-sm mt-1">
                        {formatDate(selectedProject.endDate)}
                      </p>
                    </div>
                  </div>

                  {canViewFinancials && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted rounded-sm">
                      <div>
                        <Label>Total Budget</Label>
                        <p className="text-lg">
                          ₱{safeNumber(selectedProject.budget).toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <Label>Amount Spent</Label>
                        <p className="text-lg">
                          ₱{safeNumber(selectedProject.spent).toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <Label>Material Costs</Label>
                        <p className="text-lg">
                          ₱
                          {calculateTotalMaterialCost(
                            selectedProject.id,
                          ).toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <Label>Total Revenue</Label>
                        <p className="text-lg text-green-600">
                          ₱
                          {safeNumber(selectedProject.revenue).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="documentation" className="space-y-4">
                  <div className="space-y-4">
                    <div>
                      <h4 className="mb-3">Fabricator Documentation</h4>
                      <div className="grid gap-2">
                        {getFabricatorDocumentation(selectedProject).map(
                          (doc) => (
                            <div
                              key={doc.id}
                              className="flex items-center justify-between p-3 border rounded-sm"
                            >
                              <div className="flex items-center gap-3">
                                <FileText className="h-4 w-4" />
                                <div>
                                  <p className="text-sm">{doc.name}</p>
                                  <p className="text-xs text-muted-foreground">
                                    Uploaded by{" "}
                                    {
                                      users.find((u) => u.id === doc.uploadedBy)
                                        ?.name
                                    }{" "}
                                    on {formatDate(doc.uploadedAt)}
                                  </p>
                                </div>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => downloadAttachment(doc)}
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            </div>
                          ),
                        )}
                      </div>
                    </div>

                    <div>
                      <h4 className="mb-3">All Project Documents</h4>
                      <div className="grid gap-2">
                        {getAttachments(selectedProject).map((doc) => (
                          <div
                            key={doc.id}
                            className="flex items-center justify-between p-3 border rounded-sm"
                          >
                            <div className="flex items-center gap-3">
                              <FileText className="h-4 w-4" />
                              <div>
                                <p className="text-sm">{doc.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {(safeNumber(doc.size) / 1024 / 1024).toFixed(
                                    2,
                                  )}{" "}
                                  MB - {formatDate(doc.uploadedAt)}
                                </p>
                              </div>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => downloadAttachment(doc)}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                        {getAttachments(selectedProject).length === 0 && (
                          <p className="text-sm text-muted-foreground">
                            No documents available.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="costs" className="space-y-4">
                  {canViewFinancials ? (
                    <div className="space-y-4">
                      <div>
                        <h4 className="mb-3">Cost Analysis Documents</h4>
                        <div className="grid gap-2">
                          {getCostAnalysisDocuments(selectedProject).map(
                            (doc) => (
                              <div
                                key={doc.id}
                                className="flex items-center justify-between p-3 border rounded-sm"
                              >
                                <div className="flex items-center gap-3">
                                  <PhilippinePeso className="h-4 w-4 text-green-600" />
                                  <div>
                                    <p className="text-sm">{doc.name}</p>
                                    <p className="text-xs text-muted-foreground">
                                      Created by{" "}
                                      {
                                        users.find(
                                          (u) => u.id === doc.uploadedBy,
                                        )?.name
                                      }
                                    </p>
                                  </div>
                                </div>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => downloadAttachment(doc)}
                                >
                                  <Download className="h-4 w-4" />
                                </Button>
                              </div>
                            ),
                          )}
                        </div>
                      </div>

                      <div>
                        <h4 className="mb-3">Budget Breakdown by Fabricator</h4>
                        <div className="space-y-3">
                          {(selectedProject.fabricatorBudgets || []).map(
                            (budget) => {
                              const fabricator = users.find(
                                (u) => u.id === budget.fabricatorId,
                              );
                              const allocated = safeNumber(
                                budget.allocatedAmount,
                              );
                              const spent = safeNumber(budget.spentAmount);
                              const utilizationRate =
                                allocated > 0 ? (spent / allocated) * 100 : 0;

                              return (
                                <div
                                  key={budget.fabricatorId}
                                  className="p-4 border rounded-sm"
                                >
                                  <div className="flex justify-between items-start mb-2">
                                    <div>
                                      <p className="font-medium">
                                        {fabricator?.name}
                                      </p>
                                      <p className="text-sm text-muted-foreground">
                                        {budget.description}
                                      </p>
                                    </div>
                                    <Badge
                                      variant={
                                        utilizationRate > 100
                                          ? "destructive"
                                          : "default"
                                      }
                                    >
                                      {utilizationRate.toFixed(1)}% utilized
                                    </Badge>
                                  </div>
                                  <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                      <span className="text-muted-foreground">
                                        Allocated:
                                      </span>
                                      <span className="ml-2">
                                        ₱
                                        {safeNumber(
                                          budget.allocatedAmount,
                                        ).toLocaleString()}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="text-muted-foreground">
                                        Spent:
                                      </span>
                                      <span className="ml-2">
                                        ₱
                                        {safeNumber(
                                          budget.spentAmount,
                                        ).toLocaleString()}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              );
                            },
                          )}
                        </div>
                      </div>

                      <div>
                        <h4 className="mb-3">Material Costs</h4>
                        <div className="grid gap-2">
                          {getProjectMaterials(selectedProject.id).map(
                            (material) => (
                              <div
                                key={material.id}
                                className="flex justify-between items-center p-3 border rounded-sm"
                              >
                                <div>
                                  <p className="text-sm">{material.name}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {material.quantity} {material.unit} -{" "}
                                    {material.supplier}
                                  </p>
                                </div>
                                <p className="font-medium">
                                  ₱{safeNumber(material.cost).toLocaleString()}
                                </p>
                              </div>
                            ),
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <PhilippinePeso className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg mb-2">
                        Financial Information Restricted
                      </h3>
                      <p className="text-muted-foreground">
                        Cost analysis and budget information is only available
                        to administrators.
                      </p>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="team" className="space-y-4">
                  <div className="space-y-4">
                    <div>
                      <h4 className="mb-3">Project Supervisor</h4>
                      <div className="p-4 border rounded-sm">
                        {(() => {
                          const supervisor = users.find(
                            (u) => u.id === selectedProject.supervisorId,
                          );
                          return supervisor ? (
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 rounded-sm bg-primary/10 flex items-center justify-center">
                                <User className="h-5 w-5" />
                              </div>
                              <div>
                                <p className="font-medium">{supervisor.name}</p>
                                <p className="text-sm text-muted-foreground">
                                  {supervisor.school} - {supervisor.secureId}
                                </p>
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground">
                              Supervisor information not available
                            </p>
                          );
                        })()}
                      </div>
                    </div>

                    <div>
                      <h4 className="mb-3">Fabricators</h4>
                      <div className="grid gap-3">
                        {getFabricatorIds(selectedProject).map(
                          (fabricatorId) => {
                            const fabricator = users.find(
                              (u) => u.id === fabricatorId,
                            );
                            const workLogs = getProjectWorkLogs(
                              selectedProject.id,
                            ).filter(
                              (log) => log.fabricatorId === fabricatorId,
                            );
                            const totalHours = workLogs.reduce(
                              (sum, log) => sum + log.hoursWorked,
                              0,
                            );

                            return fabricator ? (
                              <div
                                key={fabricatorId}
                                className="p-4 border rounded-sm"
                              >
                                <div className="flex items-center gap-3 mb-2">
                                  <div className="h-10 w-10 rounded-sm bg-secondary/10 flex items-center justify-center">
                                    <User className="h-5 w-5" />
                                  </div>
                                  <div className="flex-1">
                                    <p className="font-medium">
                                      {fabricator.name}
                                    </p>
                                    <p className="text-sm text-muted-foreground">
                                      {fabricator.school} -{" "}
                                      {fabricator.secureId}
                                    </p>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-sm font-medium">
                                      {totalHours} hours
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      {workLogs.length} work logs
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ) : null;
                          },
                        )}
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog (using ProjectDetails component) */}
      {showEditDialog && selectedProject && (
        <ProjectDetails
          project={selectedProject}
          users={users}
          currentUser={currentUser}
          onUpdateProject={(updated) => {
            if (onUpdateProject) onUpdateProject(updated);
            setSelectedProject(updated);
          }}
          onClose={() => {
            setShowEditDialog(false);
            setSelectedProject(null);
          }}
        />
      )}
    </div>
  );
}
